import { useEffect, useMemo, useState } from "react";
import { Lock, Search } from "lucide-react";
import { Input } from "@/components/ui/field";
import { Button } from "@/components/ui/button";
import { LoadMore } from "@/components/load-more";
import { cn } from "@/lib/cn";
import { useGroups } from "@/lib/groups";

type Scope = "all" | "selected";

/**
 * Chọn nhiều nhóm. Nhóm bị khoá gửi (chỉ admin đăng bài) vẫn hiển thị nhưng
 * không cho chọn — chọn vào chỉ tổ tạo task chắc chắn thất bại.
 *
 * Có bộ lọc "Đã chọn" vì tài khoản thật có thể có hàng trăm nhóm: chọn 2 nhóm
 * xong thì không có cách nào tìm lại chúng giữa danh sách dài.
 */
export function GroupPicker({
    accountId,
    selected,
    onChange,
}: {
    accountId: string | undefined;
    selected: string[];
    onChange: (next: string[]) => void;
}) {
    const [query, setQuery] = useState("");
    const [debounced, setDebounced] = useState("");
    const [scope, setScope] = useState<Scope>("all");
    const selectedSet = useMemo(() => new Set(selected), [selected]);

    // Tìm kiếm chạy phía server nên phải chờ người dùng gõ xong, tránh bắn
    // một request cho mỗi phím.
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(query), 300);
        return () => clearTimeout(timer);
    }, [query]);

    const groupQuery = useGroups(accountId, debounced);
    const groups = groupQuery.items;

    // Tab "đã chọn" lọc ngay trên phần đã tải; các nhóm đã chọn luôn nằm trong
    // `selected` nên không cần gọi thêm server.
    const visible = useMemo(
        () => (scope === "selected" ? groups.filter((group) => selectedSet.has(group.id)) : groups),
        [groups, scope, selectedSet],
    );

    const selectable = visible.filter((group) => !group.isSendLocked);
    const allSelected = selectable.length > 0 && selectable.every((group) => selectedSet.has(group.id));

    function toggle(groupId: string) {
        onChange(selectedSet.has(groupId) ? selected.filter((id) => id !== groupId) : [...selected, groupId]);
    }

    function toggleAll() {
        if (allSelected) {
            const visibleIds = new Set(selectable.map((group) => group.id));
            onChange(selected.filter((id) => !visibleIds.has(id)));
        } else {
            onChange([...new Set([...selected, ...selectable.map((group) => group.id)])]);
        }
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2">
                <div className="relative flex-1">
                    <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-muted" />
                    <Input
                        className="pl-9"
                        placeholder="Tìm nhóm…"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                    />
                </div>
                <Button variant="secondary" size="sm" type="button" onClick={toggleAll} disabled={selectable.length === 0}>
                    {allSelected ? "Bỏ chọn" : "Chọn tất cả"}
                </Button>
            </div>

            <div className="flex gap-1.5">
                <ScopeTab active={scope === "all"} onClick={() => setScope("all")}>
                    Tất cả ({groupQuery.total})
                </ScopeTab>
                <ScopeTab
                    active={scope === "selected"}
                    disabled={selected.length === 0}
                    onClick={() => setScope("selected")}
                >
                    Đã chọn ({selected.length})
                </ScopeTab>
            </div>

            <div className="max-h-72 divide-y divide-line overflow-y-auto rounded-lg border border-line">
                {visible.length === 0 && (
                    <p className="px-3 py-6 text-center text-sm text-ink-muted">
                        {groupQuery.isPending
                            ? "Đang tải…"
                            : scope === "selected"
                              ? "Chưa chọn nhóm nào"
                              : "Không có nhóm nào khớp"}
                    </p>
                )}

                {visible.map((group) => {
                    const checked = selectedSet.has(group.id);
                    return (
                        <label
                            key={group.id}
                            className={cn(
                                "flex items-center gap-3 px-3 py-2.5",
                                group.isSendLocked ? "cursor-not-allowed opacity-55" : "cursor-pointer hover:bg-surface-muted",
                                checked && "bg-brand-soft/50",
                            )}
                        >
                            <input
                                type="checkbox"
                                className="size-4 accent-brand"
                                checked={checked}
                                disabled={group.isSendLocked}
                                onChange={() => toggle(group.id)}
                            />
                            {group.avatarUrl ? (
                                <img src={group.avatarUrl} alt="" className="size-7 shrink-0 rounded-full" />
                            ) : (
                                <div className="size-7 shrink-0 rounded-full bg-surface-muted" />
                            )}
                            <span className="min-w-0 flex-1 truncate text-sm text-ink">{group.name}</span>
                            {group.isSendLocked && (
                                <span className="flex shrink-0 items-center gap-1 text-xs text-ink-muted">
                                    <Lock className="size-3" />
                                    khoá gửi
                                </span>
                            )}
                            <span className="shrink-0 text-xs text-ink-muted">{group.memberCount}</span>
                        </label>
                    );
                })}
            </div>

            {scope === "all" && (
                <LoadMore
                    shown={groups.length}
                    total={groupQuery.total}
                    hasMore={groupQuery.hasMore}
                    loading={groupQuery.isLoadingMore}
                    onLoadMore={groupQuery.loadMore}
                    unit="nhóm"
                />
            )}

            <div className="flex items-center justify-between text-xs text-ink-muted">
                <span>
                    Đã chọn {selected.length} nhóm
                    {query && ` · đang lọc theo "${query}"`}
                </span>
                {selected.length > 0 && (
                    <button type="button" onClick={() => onChange([])} className="text-ink-muted hover:text-danger">
                        Bỏ chọn hết
                    </button>
                )}
            </div>
        </div>
    );
}

function ScopeTab({
    active,
    disabled,
    onClick,
    children,
}: {
    active: boolean;
    disabled?: boolean;
    onClick: () => void;
    children: React.ReactNode;
}) {
    return (
        <button
            type="button"
            disabled={disabled}
            onClick={onClick}
            className={cn(
                "rounded-lg border px-2.5 py-1 text-xs transition",
                active
                    ? "border-brand bg-brand-soft font-medium text-brand"
                    : "border-line bg-surface text-ink-muted hover:text-ink",
                disabled && "cursor-not-allowed opacity-50 hover:text-ink-muted",
            )}
        >
            {children}
        </button>
    );
}
